import {useFormSubmission} from '../../contexts/form-submission-context';
import {cn, useLanguage} from '@shondalai/ui-commons';
import {Check} from 'lucide-react';

export function FormProgress() {
  const { form, progress, currentPage, goToPage, pages } = useFormSubmission();
  const { t } = useLanguage();

  if (!form || pages.length <= 1) return null;

  const showSteps = form.settings.showProgressBar !== false;

  if (!showSteps) return null;

  return (
    <div className="mb-8">
      {/* Progress Bar */}
      <div className="relative mb-6">
        <div className="overflow-hidden h-1.5 flex rounded-full bg-muted/30">
          <div
            style={{ width: `${progress.percentage}%` }}
            className={cn(
              "flex flex-col justify-center",
              "!bg-primary transition-all duration-500 ease-out rounded-full shadow-sm"
            )}
          />
        </div>
        
        {/* Progress Text */}
        <div className="flex justify-between text-[11px] text-muted-foreground/60 font-light mt-2.5">
          <span>{t('COM_EASYFORMS_STEP', 'Step')} {currentPage + 1} {t('COM_EASYFORMS_OF', 'of')} {progress.totalPages}</span>
          <span>{progress.percentage}% {t('COM_EASYFORMS_COMPLETE', 'complete')}</span>
        </div>
      </div>

      {/* Step Indicators - Minimalist Design */}
      {form.settings.showStepIndicators !== false && (
        <div className="flex items-center gap-2.5">
          {pages.map((page, index) => {
            const isActive = index === currentPage;
            const isComplete = page.isComplete;
            const isClickable = progress.visitedPages.has(index);

            return (
              <div key={page.sectionId} className="flex items-center flex-1">
                {/* Step Circle */}
                <button
                  onClick={() => isClickable && goToPage(index)}
                  disabled={!isClickable}
                  title={page.title}
                  className={cn(
                    "relative flex items-center justify-center w-8 h-8 rounded-full",
                    "border-2 transition-all duration-300 flex-shrink-0",
                    "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2",
                    isActive && "border-primary !bg-primary !text-primary-foreground scale-110 shadow-md shadow-primary/20",
                    isComplete && !isActive && "border-primary/50 bg-primary/10 text-primary",
                    !isActive && !isComplete && "border-border/40 bg-background text-muted-foreground",
                    isClickable && !isActive && "hover:border-primary/60 cursor-pointer hover:scale-105 hover:bg-muted/20",
                    !isClickable && "cursor-not-allowed opacity-40"
                  )}
                >
                  {isComplete ? (
                    <Check className="w-4 h-4" strokeWidth={2.5} />
                  ) : (
                    <span className="text-xs font-medium">{index + 1}</span>
                  )}
                </button>

                {/* Connector Line */}
                {index < pages.length - 1 && (
                  <div className={cn(
                    "h-0.5 flex-1 mx-2 transition-all duration-500",
                    isComplete ? "bg-primary/40" : "bg-border/20"
                  )} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
